import type {ReactNode} from 'react';
import React, {useEffect} from 'react';
import type {ImagePartsLightboxProps} from './image-parts-lightbox.types';
import {ChatMessagePartType} from '@Components/ai-chat-window/ai-chat-window.types';
import {GeneratedImageLightboxHeaderContent} from '@Components/ai-chat-window/components/generated-image-lightbox-header-content/generated-image-lightbox-header-content';
import {UserAttachmentLightboxHeaderContent} from '@Components/ai-chat-window/components/user-attachment-lightbox-header-content';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {getAltTextForImagePart, getUniqueKeyForImagePart, getUrlForImagePart} from '@Libraries/ai-chat.library';
import {isReattachableImagePart} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import type {AttachmentImageData} from '@Components/zoomable-lightbox/zoomable-lightbox.types';
import {ZoomableLightbox} from '@Components/zoomable-lightbox/zoomable-lightbox';

export function ImagePartsLightbox({...props}: ImagePartsLightboxProps): ReactNode {
  const currentImageIndex = props.currentImageIndex;
  const [attachmentsData, setAttachmentsData] = React.useState<AttachmentImageData[]>([]);
  const {actions} = useAiChat();

  const loadImageAttachmentsData = async (): Promise<void> => {
    const promises = props.imageParts.map(async (part) => {
      const url = await getUrlForImagePart(part);
      return {
        type: part.type,
        src: url,
        thumbSrc: url,
        uniqueKey: getUniqueKeyForImagePart(part),
        alt: getAltTextForImagePart(part),
      };
    });

    const settledResults = await Promise.allSettled(promises);
    const attachmentData: AttachmentImageData[] = settledResults
      .filter((result) => result.status === 'fulfilled')
      .map((result) => (result as PromiseFulfilledResult<AttachmentImageData>).value);
    setAttachmentsData(attachmentData);
  };

  useEffect(() => {
    void loadImageAttachmentsData();
  }, [props.imageParts]);

  useEffect(() => {
    if (props.isLightboxOpen) {
      actions.setIsSidebarExpanded(false);
    }
  }, [props.isLightboxOpen, actions]);

  if (!props.imageParts || props.imageParts.length === 0) {
    return null;
  }

  if (!props.chatId) {
    return null;
  }

  if (currentImageIndex === null || currentImageIndex < 0 || currentImageIndex >= props.imageParts.length) {
    return null;
  }

  const getCurrentImageSrc = (): string | undefined => {
    const currentAttachmentData = attachmentsData[currentImageIndex];
    return currentAttachmentData?.src;
  };

  const getLightboxHeaderContent = (): ReactNode => {
    const currentImage = props.imageParts[currentImageIndex];

    if (currentImage.type === ChatMessagePartType.MODEL_GENERATED_IMAGE) {
      return (
        <GeneratedImageLightboxHeaderContent
          canWrite={props.canWrite}
          hashedId={currentImage.hashedId}
          chatId={props.chatId}
          isOpen={props.isLightboxOpen}
          onClose={props.handleCloseLightbox}
          onReattach={props.onReattach}
          currentImage={currentImage}
          currentImageSrc={getCurrentImageSrc()}
        />
      );
    }

    if (isReattachableImagePart(currentImage)) {
      return (
        <UserAttachmentLightboxHeaderContent
          onClose={props.handleCloseLightbox}
          onReattach={props.onReattach}
          currentImage={currentImage}
          currentImageSrc={getCurrentImageSrc()}
        />
      );
    }

    return null;
  };

  return (
    <ZoomableLightbox
      headerContent={getLightboxHeaderContent()}
      isOpen={props.isLightboxOpen}
      onClose={props.handleCloseLightbox}
      imagesAttachmentData={attachmentsData}
      currentImageIndex={currentImageIndex}
      onPreviousImage={props.handlePreviousImage}
      onNextImage={props.handleNextImage}
      enableZoom
    />
  );
}
